import type {
	PageIncludeMacroParams,
	PageIncludeQueryTarget,
	ParsedPageIncludeTarget,
} from '../pageIncludeMacroTypes.ts';

/**
 * Parses macro parameters to extract the include target location.
 *
 * Resolution priority (matches backend behavior):
 * 1. Modern typed default parameter `""` (confluence-content typed link)
 * 2. Legacy `pageTitle` parameter (takes precedence over `"0"`)
 * 3. `spaceKey` + `pageTitle` joined as "SPACEKEY:Page Title"
 * 4. Legacy untyped default parameter `"0"`
 *
 * Returns a parsed location string or an error if no target can be determined.
 */
export const parseMacroTarget = (
	macroParams: PageIncludeMacroParams | undefined,
): ParsedPageIncludeTarget => {
	const referencedContentId = macroParams?.['_referencedContentId']?.value ?? null;
	const parentId = macroParams?.['_parentId']?.value ?? null;

	// 1. Modern typed parameter (key is "")
	const typedValue = macroParams?.['']?.value?.trim();
	if (typedValue) {
		return { location: typedValue, referencedContentId, parentId, error: null };
	}

	// 2. Legacy pageTitle (takes precedence over "0")
	const pageTitle = macroParams?.['pageTitle']?.value?.trim();
	if (pageTitle) {
		const spaceKey = macroParams?.['spaceKey']?.value?.trim();
		const location = spaceKey ? `${spaceKey}:${pageTitle}` : pageTitle;
		return { location, referencedContentId, parentId, error: null };
	}

	// 3. Legacy untyped default parameter "0"
	const legacyDefault = macroParams?.['0']?.value?.trim();
	if (legacyDefault) {
		return { location: legacyDefault, referencedContentId, parentId, error: null };
	}

	// No target configured
	return { location: null, referencedContentId, parentId, error: 'missing-target' };
};

const extractContentIdFromTypedValue = (value: string): string | null => {
	const match = value.match(/confluence-content:\/\/[^/]+\/(\d+)/);
	return match ? match[1] : null;
};

const BLOG_POST_LINK_REGEX = /^(?:([\w]+):)?\/(\d{4}\/\d{2}\/\d{2})\/(.+)$/;

const isValidSpaceKey = (spaceKey: string): boolean => {
	if (!spaceKey) {
		return false;
	}

	if (spaceKey.startsWith('~')) {
		return true;
	}

	return spaceKey.length <= 255 && /^[A-Za-z0-9]+$/.test(spaceKey);
};

const parseSplitPageCandidate = (location: string): { spaceKey: string; title: string } | null => {
	const colonIdx = location.indexOf(':');
	if (colonIdx < 0) {
		return null;
	}

	const spaceKey = location.slice(0, colonIdx);
	const title = location.slice(colonIdx + 1);
	if (!isValidSpaceKey(spaceKey) || title.startsWith(' ')) {
		return null;
	}

	return { spaceKey, title };
};

const parseBlogPostCandidate = (
	location: string,
	hostSpaceKey: string | undefined,
): { spaceKey: string; title: string; postingDay: string } | null => {
	const match = location.match(BLOG_POST_LINK_REGEX);
	if (!match) {
		return null;
	}

	const spaceKey = match[1] || hostSpaceKey;
	const postingDay = match[2].replace(/\//g, '-');
	const title = match[3];
	if (!spaceKey || !title) {
		return null;
	}

	return { spaceKey, title, postingDay };
};

/**
 * Converts a parsed include target into the concrete GraphQL query target.
 * Kept pure so the same logic can be reused by React hooks and future SSR query execution.
 */
export const getPageIncludeQueryTarget = (
	parsedTarget: ParsedPageIncludeTarget,
	hostSpaceKey: string | undefined,
): PageIncludeQueryTarget => {
	if (!parsedTarget.location) {
		return { kind: 'missing-target' };
	}

	const idFromTyped = extractContentIdFromTypedValue(parsedTarget.location);
	if (idFromTyped) {
		return { kind: 'by-id', variables: { contentId: idFromTyped } };
	}

	if (parsedTarget.referencedContentId) {
		return { kind: 'by-id', variables: { contentId: parsedTarget.referencedContentId } };
	}

	const rawTitle = parsedTarget.location.trim();

	if (!rawTitle) {
		return { kind: 'missing-target' };
	}

	const splitPageCandidate = parseSplitPageCandidate(rawTitle);
	const blogPostCandidate = parseBlogPostCandidate(rawTitle, hostSpaceKey);
	const includeRaw = !!hostSpaceKey;

	if (!includeRaw && !splitPageCandidate && !blogPostCandidate) {
		return { kind: 'missing-target' };
	}

	return {
		kind: 'by-title',
		variables: {
			rawSpaceKey: hostSpaceKey ?? '',
			rawTitle,
			includeRaw,
			splitSpaceKey: splitPageCandidate?.spaceKey ?? '',
			splitTitle: splitPageCandidate?.title ?? '',
			includeSplit: !!splitPageCandidate,
			blogSpaceKey: blogPostCandidate?.spaceKey ?? '',
			blogTitle: blogPostCandidate?.title ?? '',
			postingDay: blogPostCandidate?.postingDay ?? '',
			includeBlog: !!blogPostCandidate,
		},
	};
};
